import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import './LoginPage.css'; // We will use a dedicated CSS file for this page

function LoginPage({ onLogin }) {
    const [currentMode, setCurrentMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [fullName, setFullName] = useState('');
    const [phone, setPhone] = useState('');
    const [dob, setDob] = useState('');
    const [dobYear, setDobYear] = useState('');
    const [dobMonth, setDobMonth] = useState('');
    const [dobDay, setDobDay] = useState('');
    const [otp, setOtp] = useState('');
    const [newPassword, setNewPassword] = useState(''); // State for the new password
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [message, setMessage] = useState('');
    const [agreeToTerms, setAgreeToTerms] = useState(false);
    const [resendTimer, setResendTimer] = useState(60);
    const [resendDisabled, setResendDisabled] = useState(true);

    const navigate = useNavigate();

    // Environment Variables
    const APP_NAME = process.env.REACT_APP_APP_NAME;
    const LOGIN_API_BASE = process.env.REACT_APP_LOGIN_APP_API_BASE;
    const REACT_APP_API_BASE = process.env.REACT_APP_API_BASE;
    const OTP_API_BASE = process.env.REACT_APP_OTP_BASE_URL;

    // ---- Date of Birth Helpers ----
    useEffect(() => {
        if (dobYear && dobMonth && dobDay) {
            const formattedDate = `${dobYear}-${String(dobMonth).padStart(2, '0')}-${String(dobDay).padStart(2, '0')}`;
            setDob(formattedDate);
        }
    }, [dobYear, dobMonth, dobDay]);

    const generateYearOptions = () => {
        const currentYear = new Date().getFullYear();
        const years = [];
        for (let year = currentYear - 18; year >= currentYear - 100; year--) {
            years.push(<option key={year} value={year}>{year}</option>);
        }
        return years;
    };
    const generateMonthOptions = () => ["January", "February", "March", "April", "May", "June", "July", "August", "September", "October", "November", "December"].map((month, index) => <option key={index + 1} value={index + 1}>{month}</option>);
    const generateDayOptions = () => {
        if (!dobYear || !dobMonth) return [];
        const daysInMonth = new Date(dobYear, dobMonth, 0).getDate();
        const days = [];
        for (let day = 1; day <= daysInMonth; day++) {
            days.push(<option key={day} value={day}>{day}</option>);
        }
        return days;
    };

    // --- OTP Resend Timer ---
    useEffect(() => {
        let interval;
        if ((currentMode === 'login-verify-otp' || currentMode === 'signup-verify-otp') && resendDisabled) {
            setResendTimer(60);
            interval = setInterval(() => {
                setResendTimer(prev => {
                    if (prev <= 1) {
                        clearInterval(interval);
                        setResendDisabled(false);
                        return 0;
                    }
                    return prev - 1;
                });
            }, 1000);
        }
        return () => clearInterval(interval);
    }, [currentMode, resendDisabled]);

    const clearFields = () => {
        setEmail(''); setPassword(''); setFullName(''); setPhone(''); setDob('');
        setDobDay(''); setDobMonth(''); setDobYear(''); setOtp(''); setNewPassword('');
        setError(''); setMessage(''); setAgreeToTerms(false);
    };

    // ---- PRIMARY ACTION HANDLER ----
    const handleSubmit = async () => {
        if (!agreeToTerms && currentMode !== 'forgot-password' && currentMode !== 'verify-otp') {
            setError('You must agree to the Terms of Use and Privacy Policy.');
            return;
        }
        setLoading(true);
        setError('');
        setMessage('');

        try {
            if (currentMode === 'login') {
                if (!email || !password) throw new Error('Email and password are required.');
                await axios.post(`${LOGIN_API_BASE}/api/send-email-otp`, { email, appName: APP_NAME });
                setMessage(`An OTP has been sent to ${email}.`);
                setCurrentMode('login-verify-otp');
                setResendDisabled(true);

            } else if (currentMode === 'signup') {
                if (!fullName || !phone || !email || !password || !dob) throw new Error('Please fill in all fields.');
                const formattedPhone = '+91' + phone.replace(/^(\+91)?/, '');
                const payload = { phone: formattedPhone, email, appName: APP_NAME, sender_id: 'GNVISA', template_id: '188004' };
                await axios.post(`${OTP_API_BASE}/api/send-otp`, payload);
                setMessage('OTP sent to your phone. Please verify.');
                setCurrentMode('signup-verify-otp');
                setResendDisabled(true);

            } else if (currentMode === 'forgot-password') {
                if (!email) throw new Error('Email is required.');
                await axios.post(`${LOGIN_API_BASE}/api/forgot-password-otp`, { email, appName: APP_NAME });
                setMessage('If an account exists, an OTP has been sent to your email.');
                setCurrentMode('verify-otp'); // Switch to the OTP verification view
            }
        } catch (err) {
            const errorMessage = err.response?.data?.message || err.message || 'An unexpected error occurred.';
             if (errorMessage.includes('User not found for this app')) {
                setCurrentMode('signup');
                setError('User not found. Please create an account.');
             } else {
                setError(errorMessage);
             }
        } finally {
            setLoading(false);
        }
    };
    
    // ---- OTP VERIFICATION HANDLERS ----

    const handleLoginVerifyOtp = async () => {
        if (!otp) { setError('Please enter the OTP.'); return; }
        setLoading(true);
        setError('');
        try {
            const { data } = await axios.post(`${LOGIN_API_BASE}/api/save-login`, { email, password, otp, appName: APP_NAME });

            if (data && data.userId) {
                const [profileRes, formRes] = await Promise.allSettled([
                    axios.get(`${REACT_APP_API_BASE}/api/user/${data.userId}`),
                    axios.get(`${REACT_APP_API_BASE}/api/form/${data.userId}`)
                ]);

                if (profileRes.status === 'rejected') throw new Error('Failed to load user profile.');
                
                const userData = profileRes.value.data;
                const formExists = formRes.status === 'fulfilled' && formRes.value.data?._id;
                
                onLogin(userData, formExists);
            } else {
                throw new Error(data.message || 'Login failed: Invalid response from server.');
            }
    } catch (err) {
            setError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const handleSignupVerifyOtp = async () => {
        if (!otp) { setError('Please enter the OTP.'); return; }
        setLoading(true);
        setError('');
        try {
            const formattedPhone = '+91' + phone.replace(/^(\+91)?/, '');
            await axios.post(`${OTP_API_BASE}/api/verify-otp`, { phone: formattedPhone, otp, appName: APP_NAME });
            
            const nameParts = fullName.trim().split(/\s+/);
            const signupPayload = { 
                firstName: nameParts.shift() || '', 
                lastName: nameParts.join(' ') || '', 
                email, phone: formattedPhone, dob, password, appName: APP_NAME 
            };
            const { data } = await axios.post(`${LOGIN_API_BASE}/api/save-signup`, signupPayload);
            
            if (data && data.userId) {
                const profileResponse = await axios.get(`${REACT_APP_API_BASE}/api/user/${data.userId}`);
                onLogin(profileResponse.data, false);
            } else {
                throw new Error('Account creation failed.');
            }
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    // --- NEW FUNCTION: To verify OTP and reset password ---
    const handleVerifyResetOtp = async () => {
        if (!otp || !newPassword) { setError('Please enter the OTP and new password.'); return; }
        setLoading(true);
        setError('');
        try {
            await axios.post(`${LOGIN_API_BASE}/api/reset-password-otp`, { email, otp, password: newPassword, appName: APP_NAME });
            Swal.fire({ icon: 'success', title: 'Success!', text: 'Your password has been reset. Please log in.' });
            setCurrentMode('login');
            clearFields();
        } catch (err) {
            setError(err.response?.data?.message || err.message || 'An unexpected error occurred.');
        } finally {
            setLoading(false);
        }
    };

    const renderContent = () => {
        // --- LOGIN FORM ---
        if (currentMode === 'login') return (
            <>
                <div className="form-header">
                    <h2>Welcome Back</h2><p>Sign in to continue</p>
                </div>
                <div className="input-group">
                    <input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <div className="input-group">
                    <input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} />
                </div>
                <div className="disclaimer-group">
                    <input type="checkbox" id="terms" checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)} />
                    <label htmlFor="terms">I agree to the <Link to="/terms" target="_blank">Terms of Use</Link> and <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>.</label>
                </div>
                <button onClick={handleSubmit} className="auth-btn" disabled={loading}>{loading ? 'Processing...' : 'Sign In'}</button>
                <p className="sub-link" onClick={() => { setCurrentMode('forgot-password'); clearFields(); }}>Forgot Password?</p>
                <div className="mode-switch">
                    <p>Don't have an account? <span onClick={() => { setCurrentMode('signup'); clearFields(); }}>Sign up</span></p>
                </div>
            </>
        );

        // --- LOGIN OTP VERIFICATION ---
        if (currentMode === 'login-verify-otp') return (
             <>
                <div className="form-header">
                    <h2>Verify Your Login</h2><p>Enter the OTP sent to {email}</p>
                </div>
                <div className="input-group">
                    <input type="text" placeholder="Enter OTP" value={otp} onChange={(e) => setOtp(e.target.value)} />
                </div>
                <div className="resend-group">
                    <p onClick={() => {setCurrentMode('login'); clearFields();}}>← Back to Login</p>
                    {resendDisabled ? (<span>Resend OTP in {resendTimer}s</span>) : (<button>Resend OTP</button>)}
                </div>
                <button onClick={handleLoginVerifyOtp} className="auth-btn" disabled={loading}>{loading ? 'Verifying...' : 'Verify & Login'}</button>
            </>
        );

        // --- SIGNUP FORM ---
        if (currentMode === 'signup') return (
            <>
                <div className="form-header">
                    <h2>Create Account</h2><p>Join us today</p>
                </div>
                <div className="input-group"><input type="text" placeholder="Full Name" value={fullName} onChange={(e) => setFullName(e.target.value)} /></div>
                <div className="input-group"><input type="tel" placeholder="Phone Number (10 digits)" value={phone} maxLength="10" onChange={(e) => setPhone(e.target.value)} /></div>
                <div className="input-group"><input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} /></div>
                <div className="dob-group">
                    <select value={dobYear} onChange={(e) => { setDobYear(e.target.value); setDobMonth(''); setDobDay(''); }}><option value="" disabled>Year</option>{generateYearOptions()}</select>
                    <select value={dobMonth} onChange={(e) => { setDobMonth(e.target.value); setDobDay(''); }} disabled={!dobYear}><option value="" disabled>Month</option>{generateMonthOptions()}</select>
                    <select value={dobDay} onChange={(e) => setDobDay(e.target.value)} disabled={!dobMonth}><option value="" disabled>Day</option>{generateDayOptions()}</select>
                </div>
                <div className="input-group"><input type="password" placeholder="Password" value={password} onChange={(e) => setPassword(e.target.value)} /></div>
                <div className="disclaimer-group">
                    <input type="checkbox" id="terms" checked={agreeToTerms} onChange={(e) => setAgreeToTerms(e.target.checked)} />
                    <label htmlFor="terms">I agree to the <Link to="/terms" target="_blank">Terms of Use</Link> and <Link to="/privacy-policy" target="_blank">Privacy Policy</Link>.</label>
                </div>
                <button onClick={handleSubmit} className="auth-btn" disabled={loading}>{loading ? 'Processing...' : 'Create Account'}</button>
                <div className="mode-switch">
                    <p>Already have an account? <span onClick={() => { setCurrentMode('login'); clearFields(); }}>Sign in</span></p>
                </div>
            </>
        );
        
        // --- SIGNUP OTP VERIFICATION ---
        if (currentMode === 'signup-verify-otp') return (
             <>
                <div className="form-header">
                    <h2>Verify Your Phone</h2><p>Enter the OTP sent to your phone</p>
                </div>
                <div className="input-group">
                    <input type="text" placeholder="Enter OTP" value={otp} onChange={(e) => setOtp(e.target.value)} />
                </div>
                 <div className="resend-group">
                    <p onClick={() => {setCurrentMode('signup'); setOtp('');}}>← Back to Signup</p>
                    {resendDisabled ? (<span>Resend OTP in {resendTimer}s</span>) : (<button>Resend OTP</button>)}
                </div>
                <button onClick={handleSignupVerifyOtp} className="auth-btn" disabled={loading}>{loading ? 'Verifying...' : 'Verify & Create Account'}</button>
            </>
        );

        // --- FORGOT PASSWORD ---
        if (currentMode === 'forgot-password') return (
            <>
                <div className="form-header">
                    <h2>Forgot Password</h2><p>Enter your email to get a reset OTP</p>
                </div>
                <div className="input-group">
                    <input type="email" placeholder="Email Address" value={email} onChange={(e) => setEmail(e.target.value)} />
                </div>
                <button onClick={handleSubmit} className="auth-btn" disabled={loading}>{loading ? 'Sending...' : 'Send OTP'}</button>
                <div className="mode-switch">
                    <p><span onClick={() => { setCurrentMode('login'); clearFields(); }}>← Back to Sign in</span></p>
                </div>
            </>
        );

        // --- VERIFY OTP FOR PASSWORD RESET ---
        if (currentMode === 'verify-otp') return (
            <>
                <div className="form-header">
                    <h2>Reset Password</h2><p>Enter the OTP and your new password</p>
                </div>
                <div className="input-group">
                    <input type="text" placeholder="Enter OTP from email" value={otp} onChange={(e) => setOtp(e.target.value)} />
                </div>
                <div className="input-group">
                    <input type="password" placeholder="New Password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} />
                </div>
                <button onClick={handleVerifyResetOtp} className="auth-btn" disabled={loading}>{loading ? 'Resetting...' : 'Reset Password'}</button>
                <div className="mode-switch">
                    <p><span onClick={() => { setCurrentMode('login'); clearFields(); }}>← Back to Sign in</span></p>
                </div>
            </>
        );

        return null;
    };


    return (
        <div className="login-page-wrapper">
            <div className="login-form-box">
                {error && <div className="alert-box error">{error}</div>}
                {message && <div className="alert-box success">{message}</div>}

                {renderContent()}
            </div>
        </div>
    );
}

export default LoginPage;